import { useMemo } from 'react'

/**
 * useVfxClasses
 *
 * Returns extra CSS class strings to be applied to <main> and <header>.
 *
 * Sources (merged in order):
 *  1. Permanent classes from .env  (REACT_APP_VFX_MAIN_CLASS / REACT_APP_VFX_HEADER_CLASS)
 *  2. Query-string params          (?main-class=border-1&header-class=is-sticky)
 *     — only when REACT_APP_VFX_QUERY_ENABLED=true
 *
 * Allowed query-string values:
 *   main-class   → border-1 | border-2 | border-3 | border-4
 *   header-class → is-sticky
 */

const ALLOWED_MAIN_CLASSES   = new Set(['border-1', 'border-2', 'border-3', 'border-4'])
const ALLOWED_HEADER_CLASSES = new Set(['is-sticky'])

const queryEnabled = process.env.REACT_APP_VFX_QUERY_ENABLED === 'true'

function sanitize(value, allowedSet) {
  if (!value) return []
  return value
    .split(/\s+/)
    .filter(cls => allowedSet.has(cls))
}

function getQueryClasses() {
  if (!queryEnabled) return { main: [], header: [] }

  const params = new URLSearchParams(window.location.search)
  return {
    main:   sanitize(params.get('main-class'),   ALLOWED_MAIN_CLASSES),
    header: sanitize(params.get('header-class'), ALLOWED_HEADER_CLASSES),
  }
}

export function useVfxClasses() {
  return useMemo(() => {
    const envMain   = (process.env.REACT_APP_VFX_MAIN_CLASS   || '').trim().split(/\s+/).filter(Boolean)
    const envHeader = (process.env.REACT_APP_VFX_HEADER_CLASS || '').trim().split(/\s+/).filter(Boolean)

    const { main: queryMain, header: queryHeader } = getQueryClasses()

    return {
      mainClass:   [...envMain,   ...queryMain  ].join(' '),
      headerClass: [...envHeader, ...queryHeader ].join(' '),
    }
  }, [])
}
